import React from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface AIDocumentationViewerProps {
  title: string;
  content: string;
  timestamp: Date;
}

const ViewerContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  background: white;
  margin: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
`;

const ViewerHeader = styled.div`
  background: linear-gradient(135deg, #4B0082, #6A0DAD);
  color: white;
  padding: 20px;
  border-radius: 12px 12px 0 0;
  border-bottom: 1px solid #eee;
`;

const ViewerTitle = styled.h2`
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 600;
`;

const ViewerTimestamp = styled.div`
  font-size: 12px;
  opacity: 0.8;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ContentContainer = styled.div`
  flex: 1;
  padding: 24px;
  overflow-y: auto;
`;

const MarkdownContent = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  line-height: 1.6;
  color: #333;

  h1, h2, h3, h4, h5, h6 {
    color: #4B0082;
    margin-top: 24px;
    margin-bottom: 12px;
    font-weight: 600;
  }

  h1 {
    font-size: 28px;
    border-bottom: 2px solid #4B0082;
    padding-bottom: 8px;
  }

  h2 {
    font-size: 24px;
    border-bottom: 1px solid #ddd;
    padding-bottom: 6px;
  }

  h3 {
    font-size: 20px;
  }

  h4 {
    font-size: 18px;
  }

  p {
    margin-bottom: 16px;
    text-align: justify;
  }

  ul, ol {
    margin-bottom: 16px;
    padding-left: 24px;
  }

  li {
    margin-bottom: 8px;
  }

  blockquote {
    border-left: 4px solid #4B0082;
    margin: 16px 0;
    padding: 12px 16px;
    background: #f8f9fa;
    border-radius: 0 6px 6px 0;
    font-style: italic;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 16px 0;
    border-radius: 6px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #ddd;
  }

  th {
    background: #4B0082;
    color: white;
    font-weight: 600;
  }

  tr:nth-child(even) {
    background: #f8f9fa;
  }

  code {
    background: #f1f3f5;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.9em;
    color: #e83e8c;
  }

  pre {
    margin: 16px 0;
    border-radius: 8px;
    overflow: hidden;
  }

  pre code {
    background: none;
    padding: 0;
    color: inherit;
  }

  a {
    color: #4B0082;
    text-decoration: none;
    border-bottom: 1px solid transparent;
    transition: border-color 0.2s ease;

    &:hover {
      border-bottom-color: #4B0082;
    }
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 6px;
    margin: 16px 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  hr {
    border: none;
    height: 2px;
    background: linear-gradient(90deg, #4B0082, #6A0DAD);
    margin: 24px 0;
    border-radius: 1px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #666;
  font-size: 16px;
  text-align: center;
`;

const AIDocumentationViewer: React.FC<AIDocumentationViewerProps> = ({
  title,
  content,
  timestamp
}) => {
  const formatTimestamp = (date: Date) => {
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <ViewerContainer>
      <ViewerHeader>
        <ViewerTitle>{title}</ViewerTitle>
        <ViewerTimestamp>
          🕒 Generated on {formatTimestamp(timestamp)}
        </ViewerTimestamp>
      </ViewerHeader>
      
      <ContentContainer>
        {content ? (
          <MarkdownContent>
            <ReactMarkdown
              components={{
                code({ node, inline, className, children, ...props }) {
                  const match = /language-(\w+)/.exec(className || '');
                  return !inline && match ? (
                    <SyntaxHighlighter
                      style={tomorrow as any}
                      language={match[1]}
                      PreTag="div"
                      {...props}
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  ) : (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  );
                }
              }}
            >
              {content}
            </ReactMarkdown>
          </MarkdownContent>
        ) : (
          <EmptyState>
            <div>📄</div>
            <div>No content to display</div>
          </EmptyState>
        )}
      </ContentContainer>
    </ViewerContainer>
  );
};

export default AIDocumentationViewer;
